import React, { useState } from 'react';
import { Droplets, Box, ArrowUpRight } from 'lucide-react';

const WaterStaircaseStep = ({ value, onChange }) => {
  const [showLocationFor, setShowLocationFor] = useState(null);

  const elements = [
    { id: 'undergroundTank', label: 'Underground tank / borewell', icon: Droplets },
    { id: 'overheadTank', label: 'Overhead tank', icon: Box },
    { id: 'staircase', label: 'Staircase inside house', icon: ArrowUpRight }
  ];

  const gridDirections = [
    ['NW', 'N', 'NE'],
    ['W', 'Center', 'E'],
    ['SW', 'S', 'SE']
  ];

  const toggleElement = (elementId) => {
    const newValue = { ...value };
    newValue[elementId] = !newValue[elementId];
    onChange(newValue);
  };

  const setLocation = (elementKey, location) => {
    const newValue = { ...value };
    if (!newValue.tankLocations) newValue.tankLocations = {};
    newValue.tankLocations[elementKey] = location;
    onChange(newValue);
    setShowLocationFor(null);
  };

  const getLocationLabel = (elementKey) => {
    const loc = value.tankLocations?.[elementKey];
    if (!loc) return 'Set location';
    if (loc === 'unsure') return 'Not sure';
    return loc.toUpperCase();
  };

  return (
    <div>
      <h3 className="text-2xl font-bold text-[#232323] mb-2">Do you have any of these?</h3>
      <p className="text-[#353535] mb-6">Toggle the items you have, then set their approximate locations.</p>
      
      {/* Element toggles */}
      <div className="space-y-4 mb-6">
        {elements.map((element) => {
          const Icon = element.icon;
          const isEnabled = value[element.id] || false;
          const isSettingLocation = showLocationFor === element.id;
          
          return (
            <div key={element.id} className="border-2 border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Icon className="w-6 h-6 text-[#232323]" />
                  <span className="font-semibold text-[#232323]">{element.label}</span>
                </div>
                <button
                  onClick={() => toggleElement(element.id)}
                  className={`relative w-14 h-8 rounded-full transition-all ${
                    isEnabled ? 'bg-[#232323]' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${
                      isEnabled ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              
              {isEnabled && (
                <div>
                  {!isSettingLocation ? (
                    <button
                      onClick={() => setShowLocationFor(element.id)}
                      className="w-full mt-2 p-2 bg-[var(--accent-blue-200)] rounded-lg text-sm font-semibold text-[#232323] hover:bg-[var(--accent-saffron)] transition-colors"
                    >
                      📍 {getLocationLabel(element.id)}
                    </button>
                  ) : (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-[#232323] mb-2">Select location:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {gridDirections.map((row, rowIdx) => (
                          row.map((dir, colIdx) => {
                            const cellId = dir.toLowerCase().replace(' ', '-');
                            return (
                              <button
                                key={`${rowIdx}-${colIdx}`}
                                onClick={() => setLocation(element.id, cellId)}
                                disabled={dir === 'Center'}
                                className={`p-2 rounded border-2 text-xs font-bold transition-all ${
                                  dir === 'Center'
                                    ? 'opacity-50 cursor-not-allowed border-gray-200'
                                    : 'border-gray-300 hover:border-[#232323] hover:bg-[var(--accent-saffron)]'
                                }`}
                              >
                                {dir}
                              </button>
                            );
                          })
                        ))}
                      </div>
                      <button
                        onClick={() => setLocation(element.id, 'unsure')}
                        className="w-full mt-2 p-2 border-2 border-gray-300 rounded text-xs font-semibold hover:border-[#232323]"
                      >
                        Not sure / No idea
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <p className="text-sm text-[#353535]">
          💡 <strong>Tip:</strong> If you don't have any of these features, just leave them all off and continue.
        </p>
      </div>
    </div>
  );
};

export default WaterStaircaseStep;